import React, { useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Thumbs } from "swiper/modules";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/thumbs";
import "swiper/css/navigation";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import type { TransformedProduct } from "../../types/productApiTypes";

interface ProductShowcaseProps {
  product?: TransformedProduct;
  isLoading?: boolean;
  isFetching?: boolean;
  error?: any;
}

const ProductShowcase: React.FC<ProductShowcaseProps> = ({
  product,
  isLoading,
  /*   isFetching, */
  error,
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const mainSwiperRef = useRef<SwiperCore | null>(null);

  if (isLoading) {
    return (
      <div className="product-showcase__wrapper">
        <div className="product-showcase product-showcase--loading">
          <div className="product-showcase__gallery">
            <Skeleton height={350} width={350} style={{ borderRadius: 12 }} />
            <div style={{ marginTop: "1rem", display: "flex", gap: "8px" }}>
              {Array.from({ length: 4 }).map((_, idx) => (
                <Skeleton
                  key={idx}
                  width={60}
                  height={60}
                  style={{ borderRadius: 8 }}
                />
              ))}
            </div>
          </div>
          <div className="product-showcase__info">
            <Skeleton height={38} width={220} style={{ marginBottom: 16 }} />
            <Skeleton count={3} width={320} />
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="product-showcase__wrapper">
        <div className="product-showcase__error">Ürün yüklenemedi.</div>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="product-showcase__wrapper">
        <div className="product-showcase__notfound">Ürün bulunamadı.</div>
      </div>
    );
  }

  // Defensive: fallback to empty array if no images
  const images =
    product.images && product.images.length > 0 ? product.images : [];

  // Handler to sync main swiper and active index when a thumbnail is clicked
  const handleThumbClick = (idx: number) => {
    setActiveImageIndex(idx);
    if (mainSwiperRef.current) {
      mainSwiperRef.current.slideTo(idx);
    }
  };

  return (
    <div className="product-showcase__wrapper">
      <div className="product-showcase">
        <div className="product-showcase__gallery">
          <Swiper
            modules={[Thumbs]}
            spaceBetween={10}
            thumbs={{
              swiper:
                thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
            }}
            onSlideChange={(swiper) => setActiveImageIndex(swiper.activeIndex)}
            initialSlide={activeImageIndex}
            className="product-showcase__main-swiper"
            onSwiper={(swiper) => {
              mainSwiperRef.current = swiper;
            }}
          >
            {images.map((img, idx) => (
              <SwiperSlide key={`${img.id}-${idx}`}>
                <img src={img.src} alt={img.alt || product.name} />
              </SwiperSlide>
            ))}
          </Swiper>
          <Swiper
            onSwiper={setThumbsSwiper}
            spaceBetween={8}
            slidesPerView={Math.min(images.length, 5)}
            watchSlidesProgress
            className="product-showcase__thumbs-swiper"
          >
            {images.map((img, idx) => (
              <SwiperSlide
                key={`${img.id}-${idx}`}
                className={activeImageIndex === idx ? "active" : ""}
                onClick={() => handleThumbClick(idx)}
              >
                <img
                  src={img.thumbnail || img.src}
                  alt={img.alt || product.name}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className="product-showcase__info">
          {product.subtitle && (
            <h2 className="product-showcase__subtitle">{product.subtitle}</h2>
          )}
          <h1 className="product-showcase__title">{product.name}</h1>
          <div
            className="product-showcase__desc"
            dangerouslySetInnerHTML={{ __html: product.shortDescription }}
          />
          {(product.categories?.length > 0 || product.tags?.length > 0) && (
            <div className="product-showcase__meta">
              {product.categories?.length > 0 && (
                <div className="product-showcase__meta-section">
                  <span className="product-showcase__meta-label">
                    Kategoriler:
                  </span>
                  <span className="product-showcase__meta-list">
                    {product.categories.map((category, index) => (
                      <span
                        key={category.id}
                        className="product-showcase__meta-item"
                      >
                        <a href={category.link}>{category.name}</a>
                        {index < product.categories.length - 1 && ", "}
                      </span>
                    ))}
                  </span>
                </div>
              )}
              {product.tags?.length > 0 && (
                <div className="product-showcase__meta-section">
                  <span className="product-showcase__meta-label">
                    Etiketler:
                  </span>
                  <span className="product-showcase__meta-list">
                    {product.tags.map((tag, index) => (
                      <span
                        key={tag.id}
                        className="product-showcase__meta-item"
                      >
                        <a href={tag.link}>{tag.name}</a>
                        {index < product.tags.length - 1 && ", "}
                      </span>
                    ))}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductShowcase;
